import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Bot,
  Send,
  Sparkles,
  X,
  Loader2,
  Trash2,
  User as UserIcon,
  BookOpen,
  ExternalLink,
  PlayCircle,
} from "lucide-react";
import { toast } from "sonner";
import { renderMarkdown, isSafeInternalRoute } from "@/lib/markdown";
import { ArticleView } from "@/pages/LearningCenter";
import { useAuth } from "@/context/AuthContext";
import { startScreenTour } from "@/pages/LearningCenter";

/**
 * CopilotWidget — floating AI assistant that answers ERP questions in Turkish.
 * Positioned bottom-right on desktop (above nothing), and above the mobile FAB
 * on small screens. Persists session_id in sessionStorage so refresh keeps the
 * conversation, but does not persist across logouts.
 */
export default function CopilotWidget() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [activeArticle, setActiveArticle] = useState(null);
  const [sessionId, setSessionId] = useState(() =>
    sessionStorage.getItem("copilot_session_id") || ""
  );
  const scrollRef = useRef(null);
  const { can } = useAuth();
  const canAiProduct = can("products", "ai_create") && can("products", "add");

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/copilot/suggestions");
        setSuggestions(data.suggestions || []);
      } catch {}
      if (sessionId) {
        try {
          const { data } = await api.get(`/copilot/history/${sessionId}`);
          setMessages(data.messages || []);
        } catch {}
      }
    })();
  }, [open, sessionId]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;

    // AI ÜRÜN OLUŞTUR — Türkçe intent tespiti (regex + keyword fallback).
    // Eşleşirse mesajı Copilot LLM'e göndermiyoruz; taslak modalını açıyoruz.
    // Böylece hem Ürünler sayfası butonu hem Copilot komutu aynı akışı çalıştırır.
    const AI_PRODUCT_TRIGGER = /^\s*(\/(urun|ürün|product)\b|ürün\s*oluştur|urun\s*olustur|yeni\s*ürün\s*(oluştur|yap)|ai\s*ürün|ai\s*ile\s*ürün|ürün\s*yap|üretim\s*kartı\s*oluştur)/i;
    if (AI_PRODUCT_TRIGGER.test(msg)) {
      if (!canAiProduct) {
        toast.error("AI ile ürün oluşturma yetkiniz yok. Yöneticinizden 'Ürünler → AI Oluştur' iznini isteyin.");
        return;
      }
      // Prompt gövdesini ayıkla (tetikleyici kelimeyi at)
      const body = msg.replace(AI_PRODUCT_TRIGGER, "").replace(/^[\s:—-]+/, "").trim();
      window.dispatchEvent(new CustomEvent("fucan:open-ai-product", {
        detail: { prompt: body || msg }
      }));
      setInput("");
      setOpen(false);
      toast.success("AI Ürün Oluşturucu açılıyor…");
      return;
    }

    setInput("");
    setBusy(true);
    const nowIso = new Date().toISOString();
    setMessages((prev) => [
      ...prev,
      { id: `local-${Date.now()}`, role: "user", content: msg, created_at: nowIso },
    ]);
    try {
      const { data } = await api.post("/copilot/chat", {
        message: msg,
        session_id: sessionId || undefined,
      });
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        sessionStorage.setItem("copilot_session_id", data.session_id);
      }
      setMessages((prev) => [...prev, data.message]);
    } catch (err) {
      const msg = formatApiError(err);
      toast.error(typeof msg === "string" ? msg : "AI yanıt vermedi");
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setBusy(false);
    }
  };

  const clearChat = async () => {
    if (!sessionId) {
      setMessages([]);
      return;
    }
    try {
      await api.delete(`/copilot/history/${sessionId}`);
    } catch {}
    setMessages([]);
    sessionStorage.removeItem("copilot_session_id");
    setSessionId("");
    toast.success("Sohbet temizlendi");
  };

  // Action buton handler'ları — asistan cevabındaki `actions` array'ini karşılar.
  const handleAction = async (action) => {
    if (!action) return;
    if (action.kind === "open_article") {
      // Backend rol filtresini uygular — yetkisiz makale 403 döner.
      const ref = action.article_id || action.slug;
      if (!ref) return;
      try {
        const { data } = await api.get(`/learning/articles/${ref}`);
        setActiveArticle(data);
      } catch (e) {
        const err = formatApiError(e);
        toast.error(typeof err === "string" ? err : "Eğitim açılamadı");
      }
    } else if (action.kind === "open_route") {
      if (!isSafeInternalRoute(action.route)) {
        toast.error("Güvensiz yönlendirme engellendi.");
        return;
      }
      setOpen(false);
      nav(action.route);
    } else if (action.kind === "start_tour" && action.tour_id) {
      setOpen(false);
      startScreenTour(action.tour_id);
    }
  };

  return (
    <>
      {/* Launcher button — bottom-right, sits above the mobile FAB (bottom-5)
          on small screens so the two don't overlap. */}
      {!open && (
        <button
          data-testid="copilot-open-btn"
          onClick={() => setOpen(true)}
          className="fixed z-40 right-5 bottom-24 lg:bottom-6 w-14 h-14 bg-gradient-to-br from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 rounded-full shadow-2xl shadow-blue-600/40 flex items-center justify-center text-white transition-all group"
          aria-label="AI Asistan"
        >
          <Sparkles className="w-6 h-6 group-hover:rotate-12 transition-transform" strokeWidth={2.5} />
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full ring-2 ring-slate-950" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          data-testid="copilot-panel"
          className="fixed z-50 right-3 left-3 bottom-3 lg:right-6 lg:left-auto lg:bottom-6 lg:w-[420px] lg:max-h-[70vh] max-h-[85vh] bg-slate-950 border border-slate-800 rounded-sm shadow-2xl flex flex-col overflow-hidden"
        >
          {/* Header */}
          <div className="bg-slate-900 border-b border-slate-800 px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-blue-600 to-blue-700 rounded-sm flex items-center justify-center shrink-0">
              <Bot className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold text-slate-50 tracking-tight">AI Asistan</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full" />
                Fucan Copilot · Çevrimiçi
              </div>
            </div>
            <button
              data-testid="copilot-clear-btn"
              onClick={clearChat}
              className="text-slate-500 hover:text-red-400 p-1.5 rounded-sm transition-colors"
              title="Sohbeti temizle"
              aria-label="Sohbeti temizle"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button
              data-testid="copilot-close-btn"
              onClick={() => setOpen(false)}
              className="text-slate-400 hover:text-slate-100 p-1.5 rounded-sm transition-colors"
              aria-label="Kapat"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-950"
          >
            {messages.length === 0 && (
              <div className="text-center py-6">
                <div className="w-14 h-14 bg-slate-900 border border-slate-800 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Sparkles className="w-6 h-6 text-blue-500" />
                </div>
                <div className="text-sm text-slate-200 font-semibold mb-1">
                  ACUTO 360 Copilot
                </div>
                <div className="text-xs text-slate-400 max-w-xs mx-auto leading-relaxed">
                  Siparişler, stok, gecikmeler ve üretim akışı hakkında doğal dilde sorular sorun.
                </div>
              </div>
            )}

            {messages.map((m) => (
              <div
                key={m.id}
                data-testid={`copilot-msg-${m.role}`}
                className={`flex gap-2.5 ${m.role === "user" ? "flex-row-reverse" : ""}`}
              >
                <div
                  className={`w-7 h-7 rounded-sm flex items-center justify-center shrink-0 ${
                    m.role === "user"
                      ? "bg-slate-800"
                      : "bg-gradient-to-br from-blue-600 to-blue-700"
                  }`}
                >
                  {m.role === "user" ? (
                    <UserIcon className="w-3.5 h-3.5 text-slate-300" strokeWidth={2.5} />
                  ) : (
                    <Bot className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
                  )}
                </div>
                <div
                  className={`max-w-[85%] rounded-sm px-3 py-2 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-blue-600 text-white whitespace-pre-wrap"
                      : "bg-slate-900 border border-slate-800 text-slate-100"
                  }`}
                >
                  {m.role === "assistant" ? (
                    <>
                      <div
                        className="prose-copilot"
                        data-testid={`copilot-assistant-${m.id}`}
                        dangerouslySetInnerHTML={{ __html: renderMarkdown(m.content || "") }}
                      />
                      {Array.isArray(m.actions) && m.actions.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-slate-800 flex flex-wrap gap-1.5"
                             data-testid={`copilot-actions-${m.id}`}>
                          {m.actions.map((a, idx) => {
                            if (a.kind === "open_route" && !isSafeInternalRoute(a.route)) return null;
                            const icon =
                              a.kind === "open_article" ? <BookOpen className="w-3 h-3 mr-1" /> :
                              a.kind === "open_route" ? <ExternalLink className="w-3 h-3 mr-1" /> :
                              a.kind === "start_tour" ? <PlayCircle className="w-3 h-3 mr-1" /> : null;
                            const cls =
                              a.kind === "open_article"
                                ? "border-blue-700 text-blue-300 hover:bg-blue-950/40"
                                : a.kind === "open_route"
                                ? "border-blue-800 text-blue-300 hover:bg-blue-950/40"
                                : "border-emerald-800 text-emerald-300 hover:bg-emerald-950/40";
                            return (
                              <button
                                key={idx}
                                onClick={() => handleAction(a)}
                                className={`inline-flex items-center text-[11px] font-medium px-2 py-1 rounded-sm border bg-slate-950 ${cls} transition-colors`}
                                data-testid={`copilot-action-${a.kind}-${idx}`}
                              >
                                {icon}
                                {a.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </>
                  ) : (
                    m.content
                  )}
                </div>
              </div>
            ))}

            {busy && (
              <div className="flex gap-2.5" data-testid="copilot-typing">
                <div className="w-7 h-7 bg-gradient-to-br from-blue-600 to-blue-700 rounded-sm flex items-center justify-center shrink-0">
                  <Bot className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
                </div>
                <div className="bg-slate-900 border border-slate-800 rounded-sm px-3 py-2 flex items-center gap-2 text-sm text-slate-400">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-500" />
                  <span>Düşünüyor…</span>
                </div>
              </div>
            )}
          </div>

          {/* Suggestion chips (only shown when chat is empty) */}
          {messages.length === 0 && (
            <div className="px-4 pb-3 flex flex-wrap gap-2 border-t border-slate-800 pt-3">
              <button
                data-testid="copilot-ai-product-chip"
                onClick={() => send("ürün oluştur: Kısa polo yaka tişört, Lacivert-Siyah, S-M-L-XL, kumaş pamuk lakoste")}
                disabled={busy || !canAiProduct}
                title={canAiProduct ? "" : "AI ile ürün oluşturma yetkiniz yok"}
                className="text-xs px-2.5 py-1.5 bg-gradient-to-br from-fuchsia-900/40 to-purple-900/40 border border-fuchsia-800 hover:border-fuchsia-500 text-fuchsia-200 rounded-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <Sparkles className="w-3 h-3" /> AI ile Ürün Oluştur
              </button>
              {suggestions.slice(0, 3).map((s) => (
                <button
                  key={s}
                  data-testid={`copilot-suggestion-${s.slice(0, 10)}`}
                  onClick={() => send(s)}
                  disabled={busy}
                  className="text-xs px-2.5 py-1.5 bg-slate-900 border border-slate-800 hover:border-blue-600 hover:text-blue-300 text-slate-300 rounded-sm transition-colors disabled:opacity-50"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Composer */}
          <div className="border-t border-slate-800 bg-slate-950 p-3">
            <div className="flex items-end gap-2">
              <Textarea
                data-testid="copilot-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
                placeholder="Sorunuzu yazın… (Enter ile gönder)"
                rows={1}
                disabled={busy}
                className="min-h-[38px] max-h-[120px] resize-none bg-slate-900 border-slate-800 focus-visible:border-blue-600 focus-visible:ring-0 text-slate-100 placeholder:text-slate-500 rounded-sm text-sm"
              />
              <Button
                data-testid="copilot-send-btn"
                onClick={() => send()}
                disabled={busy || !input.trim()}
                size="icon"
                className="h-9 w-9 shrink-0 bg-blue-600 hover:bg-blue-500 rounded-sm"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Eğitim detay modal — asistanın "Eğitimi Aç" butonu ile açılır */}
      {activeArticle && (
        <ArticleView
          article={activeArticle}
          onClose={() => setActiveArticle(null)}
          onOpenRoute={(r) => {
            setOpen(false);
            nav(r);
          }}
          onStartTour={(tid) => {
            setOpen(false);
            startScreenTour(tid);
          }}
        />
      )}
    </>
  );
}
